<template>
  <div class="simple-list">
    <div
      v-for="(item, index) in list"
      :key="index"
      class="simple-list--item"
    >
      <slot
        :items="value"
        :item="item"
        :index="index"
        :methods="{ addItem, removeItem }"
      />
      <div
        v-if="defaultControls"
        class="flex justify-center items-center"
      >
        <BaseButtonV2
          v-if="index === 0 && canAdd"
          :disabled="addDisabled"
          color="secondary"
          small
          @click.prevent="addItem()"
        >
          <BaseIcon
            icon="fa-light fa-plus"
            :style="{
              fontSize: '20px', height: '32px', width: '32px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }"
          />
        </BaseButtonV2>
        <BaseButtonV2
          v-else-if="index !== 0 && canDelete"
          color="secondary"
          type="outlined"
          small
          @click.prevent="removeItem(index)"
        >
          <BaseIcon
            icon="fa-light fa-trash-can"
            :style="{
              fontSize: '20px', height: '32px', width: '32px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }"
          />
        </BaseButtonV2>
      </div>
    </div>
    <slot
      name="controls:bottom"
      :items="value"
      :methods="{ addItem, removeItem }"
    />
    <div
      v-if="hasError"
      class="input-hint"
    >
      <div
        class="input-hint--error"
        v-html="errorMessage"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'SimpleList',
  model: {
    prop: 'value',
    event: 'update',
  },
  props: {
    value: {
      type: [Array, Object],
      default: () => [],
    },
    default: {
      type: Object,
      default: () => ({}),
    },
    addDisabled: {
      type: Boolean,
      default: false,
    },
    canAdd: {
      type: Boolean,
      default: true,
    },
    canDelete: {
      type: Boolean,
      default: true,
    },
    error: {
      type: String,
      default: '',
    },
    defaultControls: {
      type: Boolean,
      default: true,
    },
    gap: {
      type: String,
      default: '0.625rem',
    },
  },
  data() {
    return {
      listItems: this.value,
    };
  },
  computed: {
    isArray() {
      return Array.isArray(this.value);
    },
    list() {
      return this.isArray ? this.value : Object.values(this.value);
    },
    hasError() {
      return this.error !== '' && this.error !== null;
    },
    errorMessage() {
      return this.error;
    },
    gridLayout() {
      return this.defaultControls && (this.canAdd || this.canDelete) ? '1fr 50px' : '1fr';
    },
  },
  watch: {
    value(val) {
      this.listItems = val;
    },
  },
  mounted() {
    if (!this.value.length) {
      this.addItem();
    }
  },
  methods: {
    addItem(item = JSON.parse(JSON.stringify(this.default))) {
      const index = this.isArray ? this.listItems.length : Object.values(this.listItems).length;

      if (this.isArray) {
        this.listItems.push(item);
      } else {
        this.$set(this.listItems, index, item);
      }
      this.$emit('update', this.list);

      document.activeElement.blur();
      this.$emit('add', {
        value: this.listItems,
        index,
      });
    },
    removeItem(index) {
      if (this.isArray) {
        this.listItems.splice(index, 1);
      } else {
        this.$delete(this.listItems, index);
      }
      this.$emit('update', this.list);

      this.$emit('remove', {
        value: this.listItems,
        index,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.simple-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &--item {
    display: grid;
    grid-template-columns: v-bind(gridLayout);
    gap: v-bind(gap);
    align-items: center;
  }
}

.input-hint {
    display: flex;
    justify-content: flex-end;
    grid-column-gap: 1em;
    padding: 0.25em 0;

    &--error {
        flex: 1;
        display: block;
        font-size: 12px;
        color: var(--forms-errors);
        line-height: 1.25;
    }
}
</style>
